import React, { Component } from 'react';
import Table, { ColumnProps } from 'antd/lib/table';
import umbrella from 'umbrella-storage';
import Cookies from 'js-cookie';
import { Tooltip, Modal, List, message } from 'antd';
import ConsultantService, { IConsultantInfo, IConsultantInfoList, IConsultantAttachment } from '../../service/consultant/ConsultantService';
import { getCooperationStates } from '../../components/public/CooperationStatesSelect';
import './page-consultant.less';
import { buildApiUrl, buildFileUrl, triggerDownload } from '../../utils/fileTools';

interface ConsultantTableState {
    pageData: IConsultantInfoList | null;
    sourceData: IConsultantInfo[];
    nameInputValue: string;
    expertiseInputValue: string;
    statesSelectValue: number | null;
    attachmentMap: Record<number, IConsultantAttachment[]>;
    attachmentModalVisible: boolean;
    attachmentModalLoading: boolean;
    activeAttachmentConsultantId: number | null;
    pagination: any;
}

const defaultPageSize = 5;
const getPageSize = () => umbrella.getLocalStorage('consultantPageSize') || defaultPageSize;
const splitRole = (role: number) => {
    const result = new Set<number>();
    let remain = role;
    [1000, 100, 10, 2, 1].forEach((part) => {
        if (remain >= part) {
            remain -= part;
            result.add(part);
        }
    });
    if (remain !== 0) {
        result.clear();
        result.add(role);
    }
    return result;
};
const canEditConsultant = () => {
    const role = parseInt(Cookies.get('userRole') || '0', 10);
    const roleParts = splitRole(role);
    return roleParts.has(1) || roleParts.has(100) || roleParts.has(1000);
};

class ConsultantTable extends Component<any, ConsultantTableState> {
    state: ConsultantTableState = {
        pageData: { records: [], total: 0, size: 0, current: 1, pages: 0 },
        sourceData: [],
        nameInputValue: '',
        expertiseInputValue: '',
        statesSelectValue: null,
        attachmentMap: {},
        attachmentModalVisible: false,
        attachmentModalLoading: false,
        activeAttachmentConsultantId: null,
        pagination: {
            current: 1,
            pageSize: getPageSize(),
            total: 0,
        },
    };

    getOperationColumnWidth = (buttonCount: number) => Math.max(78, buttonCount * 68 + 14);

    getOperationButtonCount = () => {
        return canEditConsultant() ? 1 : 0; // 仅“编辑”(有权限时)
    };

    getColumns = (): ColumnProps<any>[] => {
        const operationWidth = this.getOperationColumnWidth(this.getOperationButtonCount());

        return [
            {
                title: '姓名',
                width: 80,
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: IConsultantInfo) => (
                    <a href={`/#/app/consultant/view?id=${record.id}`}>{text}</a>
                ),
            },
            { title: '居住城市', width: 90, dataIndex: 'residenceCity', key: 'residenceCity' },
            { title: '语言', width: 90, dataIndex: 'languageTags', key: 'languageTags' },
            {
                title: '擅长领域/关键能力',
                width: 220,
                dataIndex: 'expertise',
                key: 'expertise',
                render: (text: string) => (
                    <Tooltip title={text} placement="topLeft">
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '80px', overflowY: 'auto' }}>{text}</div>
                    </Tooltip>
                ),
            },
            {
                title: '合作费用',
                width: 120,
                key: 'feeDetail',
                render: (record: any) => this.renderFeeDetail(record),
            },
            { title: '支付方式', width: 110, dataIndex: 'paymentMode', key: 'paymentMode' },
            {
                title: '资料数',
                width: 90,
                key: 'attachmentCount',
                render: (record: any) => this.renderAttachmentCount(record),
            },
            { title: '身份证', width: 120, dataIndex: 'idCardNo', key: 'idCardNo' },
            { title: '状态', width: 60, dataIndex: 'statesText', key: 'statesText' },
            {
                title: '操作',
                key: 'operation',
                fixed: 'right',
                width: operationWidth,
                onHeaderCell: () => ({ style: { width: operationWidth, minWidth: operationWidth, maxWidth: operationWidth } }),
                onCell: () => ({ style: { width: operationWidth, minWidth: operationWidth, maxWidth: operationWidth, whiteSpace: 'nowrap' } }),
                render: (record: IConsultantInfo) => (
                    <>
                        {canEditConsultant() && <button className="table-action-btn" onClick={() => this.handleEditButtonClick(record)}>编辑</button>}
                    </>
                ),
            },
        ];
    };

    parseFeeItems = (record: any): Array<{ content: string; amount: string }> => {
        if (record.feeItems) {
            try {
                const list = JSON.parse(record.feeItems);
                if (Array.isArray(list)) {
                    return list.map((item: any) => ({
                        content: item?.content || '',
                        amount: item?.amount !== undefined && item?.amount !== null ? String(item.amount) : '',
                    })).filter((item) => item.content);
                }
            } catch (e) {
                // no-op
            }
        }
        const fallback = [
            { content: '合作费用(1天)', amount: record.feeDay },
            { content: '合作费用(0.5天)', amount: record.feeHalfDay },
            { content: '线上辅导(1小时)', amount: record.feeOnlineHour },
            { content: '1v1教练(1小时)', amount: record.feeCoachHour },
        ].filter((item) => item.amount !== null && item.amount !== undefined);
        return fallback.map((item) => ({ content: item.content, amount: String(item.amount) }));
    };

    renderFeeDetail = (record: any) => {
        const feeItems = this.parseFeeItems(record);
        const content = feeItems.length === 0
            ? <span>暂无费用数据</span>
            : (
                <div>
                    {feeItems.map((item, index) => (
                        <div key={`${record.id}-${index}`}>{item.content}：{item.amount}</div>
                    ))}
                    {record.feeSpecialRemark && <div style={{ marginTop: 6 }}>备注：{record.feeSpecialRemark}</div>}
                </div>
            );

        return (
            <Tooltip placement="topLeft" title={content}>
                <button type="button" className="link-button">查看费用详情</button>
            </Tooltip>
        );
    };

    loadAttachmentByConsultantId = async (consultantId: number) => {
        if (this.state.attachmentMap[consultantId]) {
            return;
        }
        const response = await ConsultantService.getAttachmentByConsultantId(consultantId);
        if (response && response.data && response.data.attachmentList) {
            this.setState({
                attachmentMap: {
                    ...this.state.attachmentMap,
                    [consultantId]: response.data.attachmentList,
                },
            });
        }
    };

    handleAttachmentCountClick = async (consultantId: number) => {
        this.setState({
            attachmentModalVisible: true,
            attachmentModalLoading: true,
            activeAttachmentConsultantId: consultantId,
        });
        await this.loadAttachmentByConsultantId(consultantId);
        this.setState({ attachmentModalLoading: false });
    };

    handleDownloadAllAttachments = () => {
        const consultantId = this.state.activeAttachmentConsultantId;
        if (!consultantId) {
            return;
        }
        const list = this.state.attachmentMap[consultantId] || [];
        if (list.length === 0) {
            message.info('暂无可下载附件');
            return;
        }
        const downloadUrl = buildApiUrl(`/consultant/downloadAttachmentZip/${consultantId}`);
        triggerDownload(downloadUrl);
    };

    renderAttachmentCount = (record: any) => {
        return (
            <button
                type="button"
                className="link-button"
                onClick={() => this.handleAttachmentCountClick(record.id)}
            >
                {record.attachmentCount || 0}
            </button>
        );
    };

    getExpertiseText = (expertise?: string | null) => {
        if (!expertise) {
            return '';
        }
        try {
            const list = JSON.parse(expertise);
            if (Array.isArray(list)) {
                return list
                    .map((item: any) => {
                        if (typeof item === 'string') {
                            return item.trim();
                        }
                        if (item && typeof item === 'object') {
                            const label = (item.label || '').toString().trim();
                            return label;
                        }
                        return '';
                    })
                    .filter((item) => !!item)
                    .join('、');
            }
        } catch (e) {
            // keep raw text
        }
        return expertise;
    };

    setPaginationPage = (pageNo: number) => {
        this.setState({ pagination: { ...this.state.pagination, current: pageNo } });
    };

    getConsultantTableData = async (pageNo: number, name?: string, expertiseKeyword?: string, states?: number | null) => {
        const nextName = name !== undefined ? name : this.state.nameInputValue;
        const nextExpertiseKeyword = expertiseKeyword !== undefined ? expertiseKeyword : this.state.expertiseInputValue;
        const nextStates = states !== undefined ? states : this.state.statesSelectValue;

        const response = await ConsultantService.getConsultantList({
            queryParam: {
                name: nextName,
                expertiseKeyword: nextExpertiseKeyword,
                states: nextStates ?? undefined,
            },
            pageNo,
            pageSize: this.state.pagination.pageSize,
        });

        if (response && response.data && response.data.consultantInfoList) {
            const pageData: IConsultantInfoList = response.data.consultantInfoList;
            const records = pageData.records || [];
            const sourceData = records.map((item: any) => ({
                ...item,
                expertise: this.getExpertiseText(item.expertise),
                statesText: getCooperationStates(item.states),
            }));

            this.setState({
                pageData: { ...pageData, current: pageNo },
                sourceData,
                pagination: { ...this.state.pagination, total: pageData.total, current: pageNo },
                nameInputValue: nextName,
                expertiseInputValue: nextExpertiseKeyword,
                statesSelectValue: nextStates,
            });
        }
    };

    handleChangePage = (pageNo: number) => {
        this.getConsultantTableData(pageNo);
    };

    changePageSize = (current: number, pageSize: number) => {
        umbrella.setLocalStorage('consultantPageSize', pageSize);
        this.setState({ pagination: { ...this.state.pagination, current: 1, pageSize } }, () => {
            this.getConsultantTableData(1);
        });
    };

    handleEditButtonClick = (record: IConsultantInfo) => {
        window.location.href = `/#/app/consultant/manager?id=${record.id}`;
    };

    handleViewButtonClick = (record: IConsultantInfo) => {
        window.location.href = `/#/app/consultant/view?id=${record.id}`;
    };

    render() {
        const dataWithKeys = (this.state.sourceData || []).map((row) => ({ ...row, key: row.id }));
        const attachmentList = this.state.activeAttachmentConsultantId
            ? (this.state.attachmentMap[this.state.activeAttachmentConsultantId] || [])
            : [];

        return (
            <>
                <Table
                    size="small"
                    columns={this.getColumns()}
                    dataSource={dataWithKeys}
                    tableLayout="fixed"
                    pagination={{
                        showSizeChanger: true,
                        current: this.state.pagination.current,
                        pageSize: this.state.pagination.pageSize,
                        total: this.state.pagination.total,
                        pageSizeOptions: ['5', '10', '20', '50', '100'],
                        showTotal: (total, range) => `显示第 ${range[0]} 到第 ${range[1]} 条记录，共 ${total} 条记录`,
                        onShowSizeChange: this.changePageSize,
                        onChange: this.handleChangePage,
                    }}
                    scroll={{ x: 1500, y: 360 }}
                />

                <Modal
                    title="附件列表"
                    visible={this.state.attachmentModalVisible}
                    onCancel={() => this.setState({ attachmentModalVisible: false, activeAttachmentConsultantId: null })}
                    footer={[
                        <button
                            key="downloadAll"
                            type="button"
                            onClick={this.handleDownloadAllAttachments}
                            disabled={attachmentList.length === 0}
                        >
                            一键下载全部附件
                        </button>,
                        <button
                            key="close"
                            type="button"
                            onClick={() => this.setState({ attachmentModalVisible: false, activeAttachmentConsultantId: null })}
                        >
                            关闭
                        </button>,
                    ]}
                >
                    <List
                        loading={this.state.attachmentModalLoading}
                        dataSource={attachmentList}
                        locale={{ emptyText: '暂无附件' }}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <a
                                        key="download"
                                        href={buildFileUrl(item.fileUrl)}
                                        download={item.fileName}
                                    >
                                        下载
                                    </a>,
                                ]}
                            >
                                {item.fileName}
                            </List.Item>
                        )}
                    />
                </Modal>
            </>
        );
    }
}

export default ConsultantTable;
